import React from 'react'
import { Loading, useGetBusiness, useGetBusinessID } from '@marketing-milk/frontend'
import {
  AdCreative,
  DraftCampaign,
  PublishCampaignDTO,
  SavedAudience,
} from '@marketing-milk/interfaces'
import { Info } from '@mui/icons-material'
import {
  Alert,
  Checkbox,
  Drawer,
  FormControlLabel,
  Slider,
  Switch,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material'
import { campaignService } from 'app/services'
import { CampaignDetails } from 'components'
import { BlankPageLayout } from 'components/BlankPageLayout'
import { Step } from 'components/Stepper/Step'
import { Stepper } from 'components/Stepper/Stepper'
import { useDraftCampaign } from 'hooks/api/useDraftCampaign'
import { useSprint } from 'hooks/api/useSprint'
import { getAdCreativeGroups } from 'pages/business/campaigns/helpers/campaignForm.helpers'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getStartEndTimeText } from '../../sprint.helpers'
import { DateResolutionStrategy, resolveDraftDates } from './campaignDates.helpers'
import { StaleDraftDatesNotice } from './StaleDraftDatesNotice'

interface Props {
  draftCampaigns: DraftCampaign[]
  audiences?: SavedAudience[]
  creatives?: AdCreative[]
  isOpen: boolean
  sprintId: number
  onClose: () => void
}

export const PublishDraftCampaignsModal = ({ draftCampaigns, ...props }: Props) => {
  const history = useHistory()
  const businessId = useGetBusinessID()
  // Keep names only for the warning when a selected draft leaves the eligible list.
  const [draftSelection, setDraftSelection] = useState<Array<{ id: number; name: string }>>([])
  const [currentViewedDraftId, setCurrentViewedDraftId] = useState<number>()
  const [uploading, setUploading] = useState(false)
  const [uploadComplete, setUploadComplete] = useState(false)
  const [campaignUploadResults, setCampaignUploadResults] = useState<UploadInfo[]>([])
  const { deleteDraftCampaign } = useDraftCampaign()
  const { refetchPublishedCampaigns } = useSprint(props.sprintId)
  const business = useGetBusiness()

  // The AE's choices are modal state only — nothing is persisted back to the draft. The modal never
  // unmounts (SprintDraftCampaigns always renders it and only toggles isOpen), so choices survive
  // closing and reopening the drawer.
  const [resolutionByDraftId, setResolutionByDraftId] = useState<
    Record<number, DateResolutionStrategy>
  >({})

  // Held in state rather than read per render so previews don't drift mid-session; refreshed each
  // time the drawer opens, so a page left open across midnight re-evaluates staleness.
  const [now, setNow] = useState(() => new Date())

  // Unlike the date resolutions above, this one is reset every time the drawer opens: the modal
  // never unmounts, and live-by-default has to hold for every publish session, not just the first.
  const [publishPaused, setPublishPaused] = useState(false)

  useEffect(() => {
    if (props.isOpen) {
      setNow(new Date())
      setPublishPaused(false)
    }
  }, [props.isOpen])

  // Resolve IDs to current drafts so edits made while this drawer is closed reach
  // both the preview and publishing. Ineligible drafts stay out of both.
  const draftsById = new Map(draftCampaigns.map(draft => [draft.id, draft]))
  const selectedDrafts = draftSelection.flatMap(({ id }) => draftsById.get(id) ?? [])
  const withdrawnDrafts = draftSelection.filter(({ id }) => !draftsById.has(id))
  const currentViewedDraft =
    selectedDrafts.find(draft => draft.id === currentViewedDraftId) ?? selectedDrafts[0]

  const staleSelectedDrafts = selectedDrafts.filter(
    draft => resolveDraftDates(draft.config.campaign, undefined, now, business.timezone).stale
  )
  // A choice made earlier can expire: resolutions persist across drawer opens but `now` does not,
  // so "keep the end date" picked yesterday on a draft ending yesterday is no longer a valid
  // answer today. Checking only that some choice is set would let that publish, sending Meta a
  // start time at or after the end time.
  const hasUnresolvedStaleDrafts = staleSelectedDrafts.some(draft => {
    const strategy = resolutionByDraftId[draft.id]
    if (!strategy) return true
    if (strategy !== 'keepEnd') return false
    return !resolveDraftDates(draft.config.campaign, undefined, now, business.timezone)
      .keepEndAvailable
  })

  const isSelected = (draft: DraftCampaign) => draftSelection.some(({ id }) => id === draft.id)

  const clickDraft = (draft: DraftCampaign) =>
    setDraftSelection(selection =>
      selection.some(({ id }) => id === draft.id)
        ? selection.filter(({ id }) => id !== draft.id)
        : [...selection, { id: draft.id, name: draft.config.campaign.name }]
    )

  const onComplete = async () => {
    setUploading(true)

    await Promise.all(
      selectedDrafts.map(draft => {
        // Resolved from the same helper the notice previewed, so what the AE saw is what ships.
        const { startTime, endTime } = resolveDraftDates(
          draft.config.campaign,
          resolutionByDraftId[draft.id],
          now,
          business.timezone
        )

        // Must return the promise — without it Promise.all resolves on the next tick and the
        // "complete" screen plus refetchPublishedCampaigns fire before anything has published.
        return (
          campaignService
            // @ts-ignore
            .publishCampaign(Number(businessId), {
              ...draft.config,
              campaign: { ...draft.config.campaign, startTime, endTime },
              sprintID: props.sprintId,
              // after the spread so the AE's choice wins over anything stored in the draft's config
              publishPaused,
            })
            .then(res => {
              if (res) {
                setCampaignUploadResults(results => [
                  ...results,
                  {
                    message: ' uploaded to facebook',
                    name: draft.config.campaign.name,
                    resultType: 'success',
                  },
                ])
                deleteDraftCampaign(draft.id)
                return
              }

              setCampaignUploadResults(results => [
                ...results,
                {
                  message: 'uploaded to facebook with errors',
                  name: draft.config.campaign.name,
                  resultType: 'error',
                  description: 'error will display on the published campaigns tab',
                },
              ])
            })
            .catch(err => {
              const isTimeout = err.code === 'ECONNABORTED' || err.response?.status === 504
              setCampaignUploadResults(results => [
                ...results,
                {
                  message: isTimeout ? 'Request Timed Out' : 'Facebook Error',
                  name: draft.config.campaign.name,
                  resultType: isTimeout ? 'warning' : 'error',
                  description: isTimeout
                    ? 'The publish is likely still processing. Check the published campaigns tab shortly.'
                    : `${err?.response?.data?.message ?? err.message}`,
                },
              ])
            })
        )
      })
    )

    setUploading(false)
    setUploadComplete(true)
    refetchPublishedCampaigns()
  }

  return (
    <Drawer open={props.isOpen} onClose={props.onClose}>
      <div style={{ width: '90vw' }}>
        {!(uploadComplete || uploading) && (
          <Stepper submitButtonText="Publish Campaigns">
            <Step
              title={'Choose Campaigns'}
              completed={selectedDrafts.length > 0}
              canContinue={selectedDrafts.length > 0}
              disabled={selectedDrafts.length === 0}
            >
              <BlankPageLayout
                className="max-w-4xl"
                title="Choose Campaign To Publish"
                subtitle="Only the selected campaigns will be published to facebook"
              >
                <div className="w-full grid grid-cols-2 gap-2">
                  {draftCampaigns.map(draftCampaign => (
                    <div
                      key={draftCampaign.id}
                      className={
                        'flex justify-between items-center w-full p-4 border border-dashed rounded-md'
                      }
                    >
                      <Checkbox
                        color="primary"
                        onClick={() => {
                          clickDraft(draftCampaign)
                        }}
                        checked={isSelected(draftCampaign)}
                      />
                      <div>
                        <div className="font-semibold">{draftCampaign.config.campaign.name}</div>
                        <div className="">{getStartEndTimeText(draftCampaign.config.campaign)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </BlankPageLayout>
            </Step>
            <Step
              title={'Review & Publish'}
              onNext={onComplete}
              disabled={hasUnresolvedStaleDrafts || selectedDrafts.length === 0}
            >
              <BlankPageLayout
                title="Review Campaigns"
                subtitle="These campaigns will be published to facebook.  Make sure they are correct!"
              >
                <div className="w-full flex flex-col p-4 space-y-4">
                  {withdrawnDrafts.length > 0 && (
                    <Alert severity="warning">
                      {withdrawnDrafts.map(draft => draft.name).join(', ')} can no longer be
                      published — fix the errors listed on the draft campaign, then return here.
                    </Alert>
                  )}

                  <StaleDraftDatesNotice
                    staleDrafts={staleSelectedDrafts}
                    resolutionByDraftId={resolutionByDraftId}
                    setResolution={(draftId, strategy) =>
                      setResolutionByDraftId(prev => ({ ...prev, [draftId]: strategy }))
                    }
                    applyToAll={strategy =>
                      setResolutionByDraftId(prev => ({
                        ...prev,
                        ...Object.fromEntries(staleSelectedDrafts.map(d => [d.id, strategy])),
                      }))
                    }
                    now={now}
                    timezone={business.timezone}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={publishPaused}
                        onChange={(_, checked) => setPublishPaused(checked)}
                      />
                    }
                    label="Publish paused — review in Ads Manager before they spend"
                  />

                  <div className="space-x-4">
                    <Tabs value={currentViewedDraft?.id ?? false}>
                      {selectedDrafts.map(draftCampaign => (
                        <Tab
                          key={draftCampaign.id}
                          style={{
                            paddingLeft: '10px',
                          }}
                          value={draftCampaign.id}
                          label={draftCampaign.config.campaign.name}
                          onClick={() => setCurrentViewedDraftId(draftCampaign.id)}
                        />
                      ))}
                    </Tabs>
                  </div>

                  {currentViewedDraft ? (
                    <CampaignDetails
                      campaignDto={currentViewedDraft.config}
                      selectedCreatives={getAdCreativeGroups(
                        currentViewedDraft.config.creativeGroups,
                        props?.creatives || []
                      )}
                      selectedAudiences={props?.audiences?.filter(a =>
                        currentViewedDraft.config.audienceIDs.includes(a.id)
                      )}
                    />
                  ) : (
                    <p>Select a draft to view</p>
                  )}
                </div>
              </BlankPageLayout>
            </Step>
          </Stepper>
        )}

        {(uploadComplete || uploading) && (
          <BlankPageLayout
            title={'Publish Results Will Display Below'}
            subtitle={'This may take a few moments. Do not close this modal or refresh the page'}
          >
            <div className="p-4 space-y-4 border-b min-w-full">
              {uploading && <Loading />}
              {campaignUploadResults.map(result => (
                <div className="w-full mx-auto">
                  <Alert
                    severity={result.resultType}
                    variant="standard"
                    className="flex items-center"
                  >
                    <div className="grid grid-cols-2">
                      <p>{result.name}: </p>
                      <p>
                        {result.message} {result.description}
                      </p>
                    </div>
                  </Alert>
                </div>
              ))}

              <button
                className="p-8 border border-dashed border-gray-300 hover:bg-gray-100 flex items-center justify-center font-bold text-lg w-full my-8"
                onClick={() => {
                  toast.warn(
                    'it could take a few moments for the new sprint to display in the U.I.'
                  )
                  props.onClose()
                }}
              >
                Close Modal
              </button>
            </div>
          </BlankPageLayout>
        )}
      </div>
    </Drawer>
  )
}

type UploadInfo = {
  name: string
  resultType: 'error' | 'success' | 'warning'
  message: string
  description?: string
}
